/** Service implementation of Dexie that allow to work with indexedDB
 * Link to documentation: https://dexie.org/docs/API-Reference#quick-reference
 */

import { Injectable } from '@angular/core';
import Dexie, { Table } from 'dexie';
import {
	IAssigneeOrganization,
	IOption,
	ISelectableOptions,
} from '@shared/interfaces/selectable-options.interfaces';
import { IBuilding } from '@shared/interfaces/building.interfaces';
import { IOrganization } from '@shared/interfaces/organization.interface';
import { IAiChatMessage } from '@app/ai-chat/interfaces/ai-chat-message.interface';
import { IAiChat } from '@app/ai-chat/interfaces/ai-chat.interface';

type TableNames =
	| 'buildings'
	| 'organizations'
	| 'roomTypes'
	| 'organizationTypes'
	| 'teamTypes'
	| 'assignees'
	| 'aiChats'
	| 'aiChatMessages';

type TableItemType<T> = T extends Table<infer U, any> ? U : never;
type TableType<T extends TableNames> = Array<TableItemType<DatabaseService[T]>>;

@Injectable({
	providedIn: 'root',
})
export class DatabaseService extends Dexie {
	buildings!: Table<IBuilding, string>;
	organizations!: Table<IOrganization, string>;
	roomTypes!: Table<IOption, string>;
	organizationTypes!: Table<IOption, string>;
	teamTypes!: Table<string[], string>;
	assignees!: Table<IAssigneeOrganization, string>;
	aiChats!: Table<IAiChat, number>;
	aiChatMessages!: Table<IAiChatMessage, number>;

	private tableNamesList: TableNames[] = [
		'buildings',
		'organizations',
		'roomTypes',
		'organizationTypes',
		'teamTypes',
		'assignees',
		'aiChats',
		'aiChatMessages',
	];

	constructor() {
		super('AuDatabase');

		this.version(3).stores({
			buildings: 'id, name',
			organizations: 'id, name, ps_id',
			roomTypes: 'id',
			organizationTypes: 'id',
			// To store simple values as array of strings we need to provide key
			// by hands, or we will have troubles with putting values into DB
			// here we specify that there is no index keys
			teamTypes: '',
			assignees: 'id, name, psId',
			aiChats: '++id, name',
			aiChatMessages: '++id, chatId, content, time, sender',
		});
	}

	/**
	 * Below methods are promises, so for debug you can use `.then()`
	 * to see what data it returns
	 */
	async addItem(tableName: string, value: any) {
		await this.table(tableName)
			.add(value)
			.catch(err => console.error(err.message));
	}

	async addItemBulk(tableName: string, value: any[]) {
		await this[tableName].bulkAdd(value).catch(err => console.error(err.message));
	}

	async putItem(tableName: string, value: any) {
		await this.table(tableName)
			.put(value)
			.catch(err => console.error(err.message));
	}

	async putItemBulk(tableName: string, value: any[]) {
		await this[tableName].bulkPut(value).catch(err => console.error(err.message));
	}

	async replaceTable<T extends TableNames>(tableName: T, values: TableType<T>) {
		await this.transaction('rw', tableName, () => {
			this[tableName].clear();
			(this[tableName] as Dexie.Table<TableItemType<DatabaseService[T]>>).bulkPut(values);
		}).catch(err => console.error(err.message));
	}

	storeAppSelectableOptions(options: ISelectableOptions): void {
		const keys = Object.keys(options) as Array<keyof ISelectableOptions>;
		keys.forEach(key => {
			// As described in schema declaration (this.version.stores()) comment
			// Basically it will work as localStorage where you provide the key
			// and a value
			if (key === 'teamTypes') {
				this.teamTypes.put(options[key], 'teamTypes');
			} else {
				this.replaceTable(key, options[key]);
			}
		});
	}

	async resetDatabase() {
		await this.transaction('rw', this.tableNamesList, () => {
			this.tableNamesList.forEach(tableName => this[tableName].clear());
		});
	}
}
